import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/data";

const Wordmark = () => (
  <a
    href="#top"
    data-testid="nav-logo"
    className="flex items-center select-none"
    aria-label="ASTERA Women's Health home"
  >
    <img
      src="/astera-logo.png"
      alt="ASTERA Women's Health"
      className="h-16 lg:h-[88px] w-auto object-contain"
    />
  </a>
);

export const Navbar = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div
        className={`transition-[background-color,border-color,padding] duration-500 border-b ${
          scrolled
            ? "bg-[rgba(249,248,246,0.72)] backdrop-blur-xl border-[var(--astera-border,#EAE6E1)] py-2"
            : "bg-transparent border-transparent py-3"
        }`}
      >
        <nav className="mx-auto max-w-[1400px] px-6 lg:px-12 flex items-center justify-between">
          <Wordmark />

          <div className="hidden lg:flex items-center gap-1 rounded-full bg-[var(--astera-sand)]/60 border border-[#EAE6E1] p-1.5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="rounded-full px-5 py-2.5 text-sm font-medium tracking-wide text-[var(--astera-char)] transition-colors duration-300 hover:bg-clay hover:text-[var(--astera-bone)]"
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onBook}
              data-testid="nav-book-button"
              className="hidden sm:inline-flex items-center rounded-full bg-[var(--astera-char)] px-6 py-3 text-xs uppercase tracking-[0.18em] text-[var(--astera-bone)] transition-colors duration-300 hover:bg-clay"
            >
              Book Appointment
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-mobile-open"
              className="lg:hidden text-[var(--astera-char)]"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[var(--astera-char)] text-[var(--astera-bone)] flex flex-col p-8"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center">
              <span className="font-serif text-2xl tracking-[0.35em]">ASTERA</span>
              <button onClick={() => setOpen(false)} data-testid="nav-mobile-close" aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <div className="flex flex-col gap-6 mt-16">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  className="font-serif text-5xl"
                >
                  {l.label}
                </motion.a>
              ))}
            </div>
            <button
              onClick={() => {
                setOpen(false);
                onBook();
              }}
              className="mt-auto rounded-full bg-clay px-6 py-4 text-sm uppercase tracking-[0.18em]"
              data-testid="mobile-book-button"
            >
              Book Appointment
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
